import {Fragment} from 'react'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {MedicalDevice} from '@/features/projects/types'
import {projectMedicalDeviceToLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {useOutletContext} from 'react-router-dom'

interface ProjectMedicalDevicesDetailsProps {
    medicalDevices?: MedicalDevice[]
    isDisabled: boolean
    isLoading: boolean
    isError: boolean
    isActive: boolean
    handleCancel: () => void
    handleEdit: () => void
}

export const ProjectMedicalDevicesDetails = ({
    medicalDevices,
    isDisabled,
    isLoading,
    isError,
    isActive,
    handleCancel,
    handleEdit
}: ProjectMedicalDevicesDetailsProps) => {
    const {t} = useTranslation()
    const {isEditable} = useOutletContext<{isEditable: boolean}>()

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:medical_devices_card_title')}
            isDisabled={isDisabled}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isLoading={isLoading}
                        isActive={isActive}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                    />
                )
            }
        >
            {isLoading ? (
                <Skeleton height={20} width="100%" />
            ) : isError ? (
                <Text size="textSm" palette="danger" shade="700" weight="400">
                    {t('errors:default')}
                </Text>
            ) : medicalDevices && medicalDevices?.length > 0 ? (
                <Flexbox direction="column" gap={2} fullWidth>
                    {medicalDevices.map((medicalDevice, index) => (
                        <Fragment key={medicalDevice.id}>
                            <Grid columns={4} gap={2} fullWidth>
                                <GridItem spanColumn={3} direction="column" gap={2}>
                                    <Flexbox direction="column" gap={2}>
                                        <Text size="textSm" palette="neutral" shade="500" weight="400">
                                            {t('projects:medical_device_number', {number: index + 1})}
                                        </Text>
                                        <Text size="textSm" weight="500">
                                            {t(projectMedicalDeviceToLabel[medicalDevice.device])}
                                        </Text>
                                    </Flexbox>
                                </GridItem>
                            </Grid>
                            {index != medicalDevices.length - 1 && <Divider direction="horizontal" width="100%" />}
                        </Fragment>
                    ))}
                </Flexbox>
            ) : (
                <Text size="textSm" palette="neutral" shade="400" weight="400">
                    {t('commons:none')}
                </Text>
            )}
        </DetailsFieldsSection>
    )
}
